import { useEffect, useState } from 'react';
import { Routes, Route } from 'react-router-dom';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { LoadingSpinner } from './components/LoadingSpinner';
import DynamicPage from './components/DynamicPage';
import BlogPost from './pages/BlogPost';
import EcommerceDemo from './pages/EcommerceDemo';
import { SocialMediaPosts } from './pages/SocialMediaPosts';
import ELearningPageSimple from './pages/ELearningPageSimple';
import { blogSlug } from './config';

/**
 * Main Application Component
 * 
 * Provides the core application structure with routing, header, and footer.
 * Implements a hybrid approach supporting both WordPress CMS content and local React components.
 * 
 * @returns {JSX.Element} The main app component with routing configuration
 */
export const App = () => {
  // Application initialization state
  const [isLoading, setIsLoading] = useState(true);

  /**
   * Initialize the application on mount
   * Currently minimal, but can be extended for theme loading, auth checks, etc.
   */
  useEffect(() => {
    // Initialize app - placeholder for future initialization logic
    // Could include: theme loading, authentication checks, global state setup
    setIsLoading(false);
  }, []);

  // Show loading spinner during app initialization
  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="min-h-screen flex flex-col" data-testid="app-container">
      {/* Site header with navigation */}
      <Header />
      
      {/* Main content area with routing */}
      <main className="flex-grow" data-testid="main-content">
        <Routes>
          {/* Home route - renders DynamicPage with 'home' slug */}
          <Route path="/" element={<DynamicPage />} />
          
          {/* E-commerce demo route - showcases the complete shopping flow */}
          <Route path="/ecommerce-demo" element={<EcommerceDemo />} />
          
          {/* Social media posts route - displays posts from different platforms */}
          <Route path="/smPosts" element={<SocialMediaPosts />} />
          
          {/* eLearning page route - displays lessons in app store style */}
          <Route path="/elearning" element={<ELearningPageSimple />} />
          
          {/* Dynamic page route - handles WordPress pages and local components */}
          <Route path="/:slug" element={<DynamicPage />} />
          
          {/* Blog post route - specific handling for blog content */}
          <Route path={`/${blogSlug}/:slug`} element={<BlogPost />} />
        </Routes>
      </main>
      
      {/* Site footer */}
      <Footer />
    </div>
  );
}; 